/**
 * <s-hover-slideshow> — cycle a product card's images while hovered
 *
 * Lives inside the card's media link (see snippets/card-hover-slides.liquid).
 * [data-hover-slide] images keep their real sources in data-src / data-srcset until
 * the first hover, so idle cards never download extra photos.
 *
 * Sequence while hovered: slide 1 → slide 2 → … → featured image → slide 1 …
 * The host link gets [data-hover-active] whenever a non-featured slide is showing
 * so the featured image can fade out underneath. Mouse and pen only (touch taps
 * navigate normally). With prefers-reduced-motion, it shows slide 1 without cycling.
 *
 * Attributes:
 *   interval — milliseconds per image (default 1100, minimum 600)
 */
class SHoverSlideshow extends HTMLElement {
  #ac;
  #host = null;
  #hoverTarget = null;
  #slides = [];
  #index = 0;
  #timer = null;
  #loaded = false;

  connectedCallback() {
    this.#slides = [...this.querySelectorAll('[data-hover-slide]')];
    this.#host = this.closest('a') || this.parentElement;
    // Cards carry a full-size link overlay above the media, so listen on the whole card.
    this.#hoverTarget = this.closest('article') || this.#host;
    if (!this.#slides.length || !this.#host) return;

    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };
    this.#hoverTarget.addEventListener('pointerenter', (e) => {
      if (e.pointerType === 'touch') return;
      this.#start();
    }, opts);
    this.#hoverTarget.addEventListener('pointerleave', () => this.#stop(), opts);
  }

  disconnectedCallback() {
    this.#ac?.abort();
    clearInterval(this.#timer);
  }

  get #interval() {
    const value = Number(this.getAttribute('interval'));
    return Number.isFinite(value) && value >= 600 ? value : 1100;
  }

  #load() {
    if (this.#loaded) return;
    this.#loaded = true;
    for (const img of this.#slides) {
      if (img.dataset.srcset) img.srcset = img.dataset.srcset;
      if (img.dataset.src) img.src = img.dataset.src;
    }
  }

  #start() {
    this.#load();
    clearInterval(this.#timer);
    this.#index = 1;
    this.#render();
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    this.#timer = setInterval(() => {
      this.#index = (this.#index + 1) % (this.#slides.length + 1);
      this.#render();
    }, this.#interval);
  }

  #stop() {
    clearInterval(this.#timer);
    this.#timer = null;
    this.#index = 0;
    this.#render();
  }

  #render() {
    this.#slides.forEach((img, i) => img.toggleAttribute('data-active', i + 1 === this.#index));
    this.#host.toggleAttribute('data-hover-active', this.#index > 0);
  }
}

if (!customElements.get('s-hover-slideshow')) {
  customElements.define('s-hover-slideshow', SHoverSlideshow);
}
